import React from "react";

/**
 * MOCKUP — sketch-style whiteboard wireframe of the trader signup flow.
 * Deliberately rough so you can react to layout not visuals.
 */
export default function TraderFlowSketch() {
  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "#FDFBF6" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@500;700&display=swap');
        .hand { font-family: 'Caveat', cursive; }
        .box { border: 2.5px dashed #2b2b2b; background: #fff; }
        .arrow { color: #E26D29; }
        .label { background: #FFF1BF; padding: 2px 8px; border-radius: 4px; display:inline-block; }
      `}</style>

      <div className="max-w-6xl mx-auto">
        <h1 className="hand text-5xl text-center mb-2" style={{ color: "#11523B" }}>
          Trader signup flow — inside the app
        </h1>
        <p className="hand text-2xl text-center mb-10 text-neutral-600">
          (rough sketch — how a trader gets from "download" to "paying member")
        </p>

        {/* Three-phone flow */}
        <div className="grid md:grid-cols-3 gap-8 items-start">
          {/* PHONE 1 — In the app */}
          <div className="relative">
            <div className="hand text-xl text-center mb-3">
              <span className="label">STEP 1</span>
              <div className="mt-1">Inside the app</div>
            </div>
            <div className="box rounded-[28px] p-4 h-[500px] flex flex-col">
              <div className="hand text-lg text-center border-b-2 border-dashed border-neutral-400 pb-2 mb-3">
                📱 MyHandyHub App
              </div>
              <div className="hand text-2xl text-center mb-2" style={{ color: "#11523B" }}>
                Activate your<br/>Trader membership
              </div>
              <div className="mx-auto my-2 border-2 border-dashed border-orange-500 rounded-lg px-4 py-3 text-center">
                <div className="hand text-4xl" style={{ color: "#11523B" }}>£3.99</div>
                <div className="hand text-sm text-neutral-600">/ month</div>
                <div className="hand text-xs text-orange-700 mt-1">no lead fees</div>
              </div>
              <ul className="hand text-base mt-2 space-y-1 text-neutral-700">
                <li>✓ push alerts</li>
                <li>✓ unlimited quotes</li>
                <li>✓ verified profile</li>
              </ul>
              <div className="mt-auto">
                <div className="border-4 border-dashed rounded-xl p-3 text-center hand text-xl" style={{ borderColor: "#11523B", color: "#11523B" }}>
                  [ Continue in browser → ]
                </div>
                <div className="hand text-xs text-center mt-2 text-neutral-500">
                  (this is the ONLY button — no card form)
                </div>
              </div>
            </div>
            <div className="hand text-sm text-center mt-2 text-neutral-500">
              informational only — Apple safe ✅
            </div>
          </div>

          {/* ARROW between 1 and 2 */}
          <div className="hidden md:block absolute" style={{ display: "none" }} />

          {/* PHONE 2 — In the browser */}
          <div className="relative">
            <div className="hand text-xl text-center mb-3">
              <span className="label">STEP 2</span>
              <div className="mt-1">Phone opens the browser</div>
            </div>
            <div className="box rounded-[28px] p-4 h-[500px] flex flex-col" style={{ background: "#F4FDF7" }}>
              <div className="hand text-lg text-center border-b-2 border-dashed border-neutral-400 pb-2 mb-3">
                🌐 Safari / Chrome
              </div>
              <div className="hand text-sm text-neutral-500 text-center mb-2">
                🔒 myhandyhub.co.uk/subscribe
              </div>
              <div className="hand text-lg text-center mb-3" style={{ color: "#11523B" }}>
                MyHandyHub — Checkout
              </div>
              <div className="border-2 border-dashed border-neutral-400 rounded-lg p-3 space-y-2 hand text-base">
                <div>Card number  🔒</div>
                <div className="border-b border-dashed border-neutral-300 pb-1 text-neutral-400">
                  4242 4242 4242 4242
                </div>
                <div className="flex gap-2 text-neutral-400 text-sm">
                  <span>MM/YY</span><span>CVC</span><span>Postcode</span>
                </div>
              </div>
              <div className="hand text-sm text-center mt-3 text-neutral-500">
                (Stripe hosted checkout)
              </div>
              <div className="mt-auto">
                <div className="border-4 border-dashed rounded-xl p-3 text-center hand text-xl" style={{ borderColor: "#E26D29", color: "#7A3A0B" }}>
                  [ Pay £3.99 ]
                </div>
                <div className="hand text-xs text-center mt-2 text-neutral-500">
                  100% of £3.99 lands in your Stripe
                </div>
              </div>
            </div>
            <div className="hand text-sm text-center mt-2 text-neutral-500">
              you keep 100% (no Apple 30% cut) ✅
            </div>
          </div>

          {/* PHONE 3 — Back in app */}
          <div className="relative">
            <div className="hand text-xl text-center mb-3">
              <span className="label">STEP 3</span>
              <div className="mt-1">Back in the app</div>
            </div>
            <div className="box rounded-[28px] p-4 h-[500px] flex flex-col">
              <div className="hand text-lg text-center border-b-2 border-dashed border-neutral-400 pb-2 mb-3">
                📱 MyHandyHub App
              </div>
              <div className="hand text-2xl text-center" style={{ color: "#11523B" }}>
                Welcome, James 👋
              </div>
              <div className="hand text-sm text-center text-neutral-500 mb-3">
                Smith Plumbing · Manchester
              </div>
              <div className="border-2 border-dashed border-green-600 rounded-lg p-2 mb-3 hand text-center text-sm" style={{ color: "#11523B", background: "#F4FDF7" }}>
                ✓ Member — next renewal 15 Aug
              </div>
              <div className="hand text-base mb-1" style={{ color: "#11523B" }}>Live job alerts</div>
              <div className="border-2 border-dashed border-neutral-400 rounded-lg p-2 mb-2 text-sm hand">
                🔧 Leaking tap — Manchester<br/>
                <span className="text-neutral-500">£120 budget · 2h ago</span>
              </div>
              <div className="border-2 border-dashed border-neutral-400 rounded-lg p-2 text-sm hand">
                🚿 Bathroom refit — Oldham<br/>
                <span className="text-neutral-500">£1,500 budget · 5h ago</span>
              </div>
              <div className="mt-auto hand text-center text-sm text-neutral-500">
                Full dashboard unlocked automatically
              </div>
            </div>
            <div className="hand text-sm text-center mt-2 text-neutral-500">
              exact same web experience 🎉
            </div>
          </div>
        </div>

        {/* Big arrows between phones (visible on md+) */}
        <div className="hidden md:flex items-center justify-around mt-6 -mt-[280px] pointer-events-none">
          <div />
          <div className="hand text-6xl arrow">→</div>
          <div />
          <div className="hand text-6xl arrow">→</div>
          <div />
        </div>

        {/* Legend */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 hand text-lg text-neutral-700">
          <div className="border-2 border-dashed border-green-700 rounded-xl p-4">
            <div className="text-2xl mb-1" style={{ color: "#11523B" }}>What we build</div>
            "Continue in browser" button<br/>
            + subscription-status check on app open<br/>
            + "you're a member" state
          </div>
          <div className="border-2 border-dashed border-orange-500 rounded-xl p-4">
            <div className="text-2xl mb-1" style={{ color: "#7A3A0B" }}>What already exists</div>
            Stripe checkout page on the web<br/>
            + user subscription table in Mongo<br/>
            + Stripe webhook flipping "active"
          </div>
          <div className="border-2 border-dashed border-neutral-500 rounded-xl p-4">
            <div className="text-2xl mb-1" style={{ color: "#28372F" }}>The whole flow</div>
            ~15 seconds<br/>
            zero third-party fees<br/>
            no App Store rejections
          </div>
        </div>

        <p className="hand text-2xl text-center mt-12 text-neutral-600">
          MOCKUP · /mockups/trader-subscribe-flow
        </p>
      </div>
    </div>
  );
}
